import { InfoPage } from '@/components/info-page';
import { getCoachSessionPaymentAsCoach, getMeCoach } from '@/lib/api-calls';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { InfoCard } from '@/components/info-card';
import { InfoKeyValue } from '@/components/info-key-value';
import { airColumnHelper } from '@/lib/tables';
import {
  CoachPaymentSessionDetails,
  coachPaymentSessionDetailsSchema,
  PayCycleDTO,
  VideoConferenceTypeLabels,
} from '@airreading/common/data';
import { EntityTable } from '@/components/table/entity-table';
import { useReactTable, getCoreRowModel } from '@tanstack/react-table';
import { TableErrorState } from '@/components/table/table-error-state';
import { formatInstant, formatPlainDate } from '@airreading/common';
import { formatCurrency } from '@/lib/common-utils';
import { SessionCompletionStatusCell } from '@/routes/_layout/sessions/-components/session-completion-status-cell';
import { SessionFeedbackStatus } from '@/components/session/session-feedback-status';
import { useErrorToast } from '@/lib/error-handling';
import { paginationRequestSchema } from '@/lib/pagination';
import { usePaginatedFilters } from '@/lib/filtering';
import { useTimeZone } from '@/auth/useTimeZone';
import { TimeZoneTruncated } from '@/components/time-zone-truncated';
import { PaymentsTaxInfo } from '../../-components/payments-tax-info';

export const Route = createFileRoute('/_layout/payments/teaching/$payCycleId/')({
  component: TeachSessionPaymentsDetails,
  validateSearch: paginationRequestSchema,
});

const columnHelper = airColumnHelper<CoachPaymentSessionDetails>(coachPaymentSessionDetailsSchema.keyof());

const coachPaymentReportTableColumns = (timeZone: string) => [
  columnHelper.accessor((row) => row, {
    id: 'classTime',
    header: () => (
      <span className="flex items-center whitespace-nowrap">
        Time (<TimeZoneTruncated timeZone={timeZone} />)
      </span>
    ),

    cell: ({ getValue }) => {
      const session = getValue();
      if (!session) {
        return '';
      }

      const startDateTime = formatInstant(session.startInstant, timeZone);
      const endDateTime = formatInstant(session.endInstant, timeZone);

      const [month, day] = startDateTime.formattedDate.split('/');

      return `${month}/${day}, ${startDateTime.formattedTime} - ${endDateTime.formattedTime}`;
    },
  }),

  columnHelper.accessor((row) => row, {
    id: 'duration',
    header: 'Class duration',

    cell: ({ getValue }) => {
      const session = getValue();
      if (!session) {
        return '';
      }

      const duration = session.endInstant.since(session.startInstant);

      const minutes = duration.total({ unit: 'minute' });

      return `${minutes} mins (${VideoConferenceTypeLabels[session.videoConferenceType]})`;
    },
  }),

  columnHelper.accessor((row) => row, {
    id: 'meetingStatus',
    header: 'Class status',
    cell: ({ getValue }) => {
      const session = getValue();
      if (!session) {
        return '';
      }

      const { sessionCompletionStatus, coachSessionRemarksV2 } = session;

      return (
        <SessionCompletionStatusCell
          status={sessionCompletionStatus}
          coachSessionRemarksV2={coachSessionRemarksV2}
          session={session}
        />
      );
    },
  }),

  columnHelper.accessor((row) => row, {
    id: 'feedback',
    header: 'Feedback',
    cell: ({ getValue }) => {
      const session = getValue();
      if (!session) {
        return '';
      }

      const { hoursCanceledBeforeStart, sessionFeedbacks, lesson } = session;

      return (
        <SessionFeedbackStatus
          feedbacks={sessionFeedbacks || []}
          hoursCanceledBeforeStart={hoursCanceledBeforeStart}
          lessonShouldSkipFeedback={lesson.shouldSkipFeedback}
          lateSentSuffixLabel="(late)"
        />
      );
    },
  }),

  columnHelper.accessor((row) => row, {
    id: 'pay',
    header: 'Pay',

    cell: ({ getValue }) => {
      const session = getValue();

      return formatCurrency((session?.baseAmount ?? 0) / 100);
    },
  }),

  columnHelper.accessor((row) => row, {
    id: 'incentive',
    header: 'Incentive',

    cell: ({ getValue }) => {
      const session = getValue();

      return formatCurrency((session?.incentivesAmount ?? 0) / 100);
    },
  }),

  columnHelper.accessor((row) => row, {
    id: 'total',
    header: 'Total',

    cell: ({ getValue }) => {
      const session = getValue();

      const total = session?.baseAmount + session?.incentivesAmount;

      return formatCurrency((total ?? 0) / 100);
    },
  }),
];

function TeachSessionPaymentsDetails() {
  const { payCycleId } = Route.useParams();

  const { timeZone } = useTimeZone();

  const { pageIndex, pageSize, onPaginationChange } = usePaginatedFilters(Route.id, ['pageIndex', 'pageSize']);

  const { data: me } = useQuery(getMeCoach());
  const teacherName = me?.fullNameOrEmail ?? '';
  const last4Ssn = me?.last4Ssn;

  const { data, error, isPending, refetch } = useQuery(
    getCoachSessionPaymentAsCoach({
      filters: {
        pageIndex,
        pageSize,
        filters: {
          payCycleId,
        },
      },
    }),
  );

  useErrorToast(error, 'Error loading payments. Please try again');

  const formatHours = (value: number | undefined) => Math.round((value ?? 0) * 100) / 100;
  const hoursWorked30MinFormatted = formatHours(data?.totalScheduledSessionHours['30min']);
  const hoursWorked40MinFormatted = formatHours(data?.totalScheduledSessionHours['40min']);
  const totalBaseEarnings30MinFormatted = formatCurrency((data?.totalBaseAmountInCents['30min'] ?? 0) / 100);
  const totalBaseEarnings40MinFormatted = formatCurrency((data?.totalBaseAmountInCents['40min'] ?? 0) / 100);
  const totalIncentives30MinFormatted = formatCurrency((data?.totalIncentivesAmountInCents['30min'] ?? 0) / 100);
  const totalIncentives40MinFormatted = formatCurrency((data?.totalIncentivesAmountInCents['40min'] ?? 0) / 100);
  const grossEarningsFormatted = formatCurrency((data?.totalAmountInCents ?? 0) / 100);

  const table = useReactTable({
    data: data?.payments || [],
    columns: coachPaymentReportTableColumns(timeZone),
    getRowId: (row) => row.id,
    rowCount: data?.total || 0,
    getCoreRowModel: getCoreRowModel(),
    onPaginationChange,
    manualPagination: true,
    state: {
      pagination: {
        pageIndex: pageIndex,
        pageSize: pageSize,
      },
    },
  });

  return (
    <InfoPage
      headerText={'Payments'}
      data-testid="payments-teaching-page"
      breadcrumbs={{
        linkItems: [
          {
            label: 'Payment',
            link: {
              to: '/payments',
            },
          },
        ],
        currentPage: formatPlainDate(PayCycleDTO.fromPayPeriodId(payCycleId).paymentPlainDate),
      }}
    >
      <PaymentsTaxInfo teacherName={teacherName} last4Ssn={last4Ssn} data-testid="payments-teaching-tax-info" />
      <div className="grid grid-cols-3 gap-4 w-full">
        <div className="col-span-1 min-w-0">
          <InfoCard headerText="" maxItemsPerRow={1} data-testid="payments-teaching-summary">
            <InfoKeyValue
              label={'Period'}
              value={`
              ${formatPlainDate(PayCycleDTO.fromPayPeriodId(payCycleId).startPlainDate)} - ${formatPlainDate(PayCycleDTO.fromPayPeriodId(payCycleId).endPlainDate)}
              `}
              data-testid="payments-teaching-summary-period"
            />
            <InfoKeyValue
              label={'Pay initiation date'}
              value={formatPlainDate(PayCycleDTO.fromPayPeriodId(payCycleId).paymentPlainDate)}
              data-testid="payments-teaching-summary-pay-initiation-date"
            />
            <InfoKeyValue
              label={'Delivery (1-3 business days)'}
              value={data?.depositPostedDate}
              data-testid="payments-teaching-summary-delivery"
            />
          </InfoCard>
        </div>
        <div className="col-span-2 min-w-0">
          <InfoCard headerText="" maxItemsPerRow={4} data-testid="payments-earnings-summary">
            <InfoKeyValue
              label="Hours taught (30 min class)"
              value={hoursWorked30MinFormatted}
              data-testid="payments-earnings-summary-hours-taught-30min"
            />
            <InfoKeyValue
              label="Hourly rate (30 min class)"
              value="$16.00"
              data-testid="payments-earnings-summary-hourly-rate-30min"
            />
            <InfoKeyValue
              label="Total base earnings (30 min class)"
              value={totalBaseEarnings30MinFormatted}
              data-testid="payments-earnings-summary-total-base-earnings-30min"
            />
            <InfoKeyValue
              label="Total incentives (30 min class)"
              value={totalIncentives30MinFormatted}
              data-testid="payments-earnings-summary-total-incentives-30min"
            />
            <InfoKeyValue
              label="Hours taught (40 min class)"
              value={hoursWorked40MinFormatted}
              data-testid="payments-earnings-summary-hours-taught-40min"
            />
            <InfoKeyValue
              label="Hourly rate (40 min class)"
              value="$18.00"
              data-testid="payments-earnings-summary-hourly-rate-40min"
            />
            <InfoKeyValue
              label="Total base earnings (40 min class)"
              value={totalBaseEarnings40MinFormatted}
              data-testid="payments-earnings-summary-total-base-earnings-40min"
            />
            <InfoKeyValue
              label="Total incentives (40 min class)"
              value={totalIncentives40MinFormatted}
              data-testid="payments-earnings-summary-total-incentives-40min"
            />
            <div aria-hidden />
            <InfoKeyValue
              label="Gross earnings"
              value={grossEarningsFormatted}
              data-testid="payments-earnings-summary-gross-earnings"
            />
            <InfoKeyValue
              label="Total deductions"
              value="$0"
              data-testid="payments-earnings-summary-total-deductions"
            />
            <InfoKeyValue
              label="Net earnings"
              value={grossEarningsFormatted}
              data-testid="payments-earnings-summary-net-earnings"
            />
          </InfoCard>
        </div>
      </div>
      <EntityTable
        table={table}
        showPagination
        data-testid="payments-teaching-table"
        rowTestId={(_, rowIndex) => `payments-teaching-table-row-${rowIndex}`}
        cellTestId={(_, columnId, rowIndex) => `payments-teaching-table-cell-${columnId}-${rowIndex}`}
        endMessageTestId="payments-teaching-table-end-message"
        isLoading={isPending}
        hasError={!!error}
        emptyState="No payments found"
        errorState={<TableErrorState refetchAction={refetch} />}
      />
    </InfoPage>
  );
}
